import React, { useState, useMemo, useEffect } from 'react';
import { 
  BookOpen, GraduationCap, Users, School, Calendar, 
  Building, Notebook, Pencil, Trash, Eye, Search, 
  Filter, Download, Upload, Plus, ChevronDown, CheckCircle, Clock, X, MoreVertical, AlertTriangle, ShieldAlert, Check
} from 'lucide-react';
import { 
  PieChart, Pie, Cell, Tooltip as RechartsTooltip, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid
} from 'recharts';
import subjectService from '../../services/SubjectService.js';
import { getNormalizedInstitutionType, getInstitutionTerminology, INSTITUTION_TYPES } from '../../config/institutionConfig.js';
import academicSectionService from '../../services/AcademicSectionService.js';
import courseService from '../../services/CourseService.js';
import departmentService from '../../services/DepartmentService.js';

// --- STYLES FOR ENTERPRISE ERP SCALE & READABILITY ---
const styles = {
  container: {
    backgroundColor: '#F8FAFC',
    minHeight: '100vh',
    padding: '24px 28px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    color: '#0F172A',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px'
  },
  title: {
    fontSize: '24px',
    fontWeight: '800',
    color: '#0F172A',
    margin: '0 0 4px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  subtitle: {
    fontSize: '13.5px',
    color: '#64748B',
    margin: 0
  },
  headerActions: {
    display: 'flex',
    gap: '12px',
    alignItems: 'center'
  },
  btnPrimary: {
    backgroundColor: '#2563EB',
    color: 'white',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '10px',
    fontSize: '13.5px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.2)',
    transition: 'all 0.2s ease',
  },
  btnSecondary: {
    backgroundColor: 'white',
    color: '#334155',
    border: '1px solid #CBD5E1',
    padding: '10px 16px',
    borderRadius: '10px',
    fontSize: '13.5px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.2s ease',
  },
  gridCards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '18px',
    marginBottom: '24px'
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '14px',
    padding: '20px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05), 0 2px 4px -1px rgba(0, 0, 0, 0.03)',
    border: '1px solid rgba(226, 232, 240, 0.8)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  },
  iconBox: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '14px'
  },
  cardNumber: {
    fontSize: '26px',
    fontWeight: '800',
    color: '#0F172A',
    margin: '0 0 2px 0'
  },
  cardTitle: {
    fontSize: '13.5px',
    fontWeight: '600',
    color: '#64748B',
    margin: 0
  },
  filtersContainer: {
    backgroundColor: 'white',
    borderRadius: '14px',
    padding: '18px 20px',
    marginBottom: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    border: '1px solid #E2E8F0',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '14px',
    alignItems: 'center'
  },
  searchInput: {
    padding: '11px 16px 11px 42px',
    borderRadius: '10px',
    border: '1px solid #CBD5E1',
    backgroundColor: '#F8FAFC',
    fontSize: '14px',
    width: '300px',
    outline: 'none',
    transition: 'border-color 0.2s ease',
  },
  select: {
    padding: '11px 38px 11px 16px',
    borderRadius: '10px',
    border: '1px solid #CBD5E1',
    backgroundColor: '#F8FAFC',
    fontSize: '14px',
    color: '#334155',
    outline: 'none',
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2364748B' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 14px center',
  },
  tableContainer: {
    backgroundColor: 'white',
    borderRadius: '14px',
    overflow: 'hidden',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
    border: '1px solid #E2E8F0',
    marginBottom: '28px'
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left'
  },
  th: {
    padding: '14px 18px',
    fontSize: '12.5px',
    fontWeight: '700',
    color: '#64748B',
    textTransform: 'uppercase',
    letterSpacing: '0.04em',
    borderBottom: '2px solid #E2E8F0',
    backgroundColor: '#F8FAFC',
    position: 'sticky',
    top: 0,
    zIndex: 10
  },
  td: {
    padding: '14px 18px',
    fontSize: '14px',
    color: '#334155',
    borderBottom: '1px solid #F1F5F9',
  },
  badge: {
    padding: '5px 14px',
    borderRadius: '20px',
    fontSize: '12px',
    fontWeight: '600',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px'
  },
  drawer: {
    position: 'fixed',
    top: 0,
    right: 0,
    width: '540px',
    height: '100vh',
    backgroundColor: 'white',
    boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
    zIndex: 1000,
    transform: 'translateX(100%)',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    display: 'flex',
    flexDirection: 'column'
  },
  drawerOpen: {
    transform: 'translateX(0)',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(15, 23, 42, 0.4)',
    backdropFilter: 'blur(4px)',
    zIndex: 999,
    opacity: 0,
    pointerEvents: 'none',
    transition: 'opacity 0.3s ease'
  },
  overlayOpen: {
    opacity: 1,
    pointerEvents: 'auto'
  }
};

const SubjectManagement = ({ 
  subjects = [], 
  setSubjects, 
  teachers = [], 
  classes = [],
  orgData = null,
  institutionType: propInstitutionType = null,
  terminology: propTerminology = null,
  onEdit, 
  onAddSubject, 
  onDeleteSubject,
  onAssignTeacher 
}) => {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('All');
  const [statusFilter, setStatusFilter] = useState('All');
  const [classFilter, setClassFilter] = useState('All');
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [activeSubjectTab, setActiveSubjectTab] = useState('master');

  // Institution Normalization - Standard School ERP
  const institutionType = INSTITUTION_TYPES.SCHOOL;
  const terminology = getInstitutionTerminology(INSTITUTION_TYPES.SCHOOL);
  const isDegreeCollege = false;
  const isPuc = false;
  const isSchool = true;

  // College-specific degree programs, departments & academic sections
  const [academicSectionsList, setAcademicSectionsList] = useState([]);
  const [programsList, setProgramsList] = useState([]);
  const [departmentsList, setDepartmentsList] = useState([]);

  useEffect(() => {
    if (isDegreeCollege) {
      academicSectionService.getAllSections().then(res => {
        const list = Array.isArray(res) ? res : (res?.sections || res?.data || []);
        setAcademicSectionsList(list);
      }).catch(() => {});

      courseService.getAllCourses().then(res => {
        const list = Array.isArray(res) ? res : (res?.courses || res?.data || []);
        setProgramsList(list);
      }).catch(() => {});

      departmentService.getAllDepartments().then(res => {
        const list = Array.isArray(res) ? res : (res?.departments || res?.data || []);
        setDepartmentsList(list);
      }).catch(() => {});
    }
  }, [isDegreeCollege]);
  
  // Persisted Database Allocations State for current open subject
  const [drawerAllocations, setDrawerAllocations] = useState([]);
  const [loadingAllocations, setLoadingAllocations] = useState(false);

  // Real database allocations state for the whole organization
  const [orgAllocations, setOrgAllocations] = useState([]);
  const [loadingOrgAllocations, setLoadingOrgAllocations] = useState(false);

  const getOrgId = () => {
    try {
      const orgDataStr = localStorage.getItem('orgData');
      if (orgDataStr) {
        const orgDataObj = JSON.parse(orgDataStr);
        return orgDataObj.orgId || orgDataObj.id || 'SCH-02';
      }
    } catch (e) {
      console.error(e);
    }
    return 'SCH-02';
  };

  const fetchOrgAllocations = async () => {
    const orgId = getOrgId();
    setLoadingOrgAllocations(true);
    try {
      const res = await subjectService.getOrgSubjectAllocations(orgId, '2026-2027');
      if (Array.isArray(res)) {
        setOrgAllocations(res);
      }
    } catch (err) {
      console.error('Failed to load org subject allocations:', err);
    } finally {
      setLoadingOrgAllocations(false);
    }
  };

  useEffect(() => {
    fetchOrgAllocations();
  }, [activeSubjectTab]);

  const getSubjectTeacher = (sub) => {
    if (!sub) return '';
    if (sub.teacher && sub.teacher.trim()) return sub.teacher;
    if (sub.classTeacherName && sub.classTeacherName.trim()) return sub.classTeacherName;
    if (sub.assignedTeacher && sub.assignedTeacher.trim()) return sub.assignedTeacher;
    if (orgAllocations && Array.isArray(orgAllocations)) {
      const match = orgAllocations.find(a => String(a.subjectId) === String(sub.subjectId) && a.teacherName);
      if (match && match.teacherName) return match.teacherName;
    }
    return '';
  };

  const handleDeleteAllocation = async (allocationId) => {
    if (!window.confirm('Are you sure you want to delete this subject allocation?')) return;
    try {
      const res = await subjectService.deleteSubjectAllocation(allocationId);
      if (res && res.success) {
        alert('Subject allocation deleted successfully!');
        fetchOrgAllocations();
      } else {
        alert('Failed to delete allocation: ' + (res.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      alert('Error deleting allocation: ' + err.message);
    }
  };

  const normalizeSection = (s) => (s || '').toString().replace(/^section\s*/i, '').trim();
  const normalizeClass = (c) => (c || '').toString().trim();

  const handleDeleteClassAllocations = async (className, sectionName) => {
    const targetLabel = isDegreeCollege ? 'Semester' : isPuc ? 'Stream' : 'Class';
    const cleanSec = normalizeSection(sectionName) || 'A';
    const cleanCls = normalizeClass(className);
    if (!window.confirm(`Are you sure you want to delete all subject allocations for ${cleanCls} Section ${cleanSec}?`)) return;
    try {
      setLoadingOrgAllocations(true);
      const res = await subjectService.deleteClassSectionAllocations(cleanCls, cleanSec, '2026-2027');
      if (res && res.success) {
        alert(`All allocations for this ${targetLabel.toLowerCase()} section deleted successfully!`);
        fetchOrgAllocations();
      } else {
        alert('Failed to delete allocations: ' + (res.error || 'Unknown error'));
      }
    } catch (err) {
      console.error(err);
      alert('Error deleting allocations: ' + err.message);
    } finally {
      setLoadingOrgAllocations(false);
    }
  };

  const getGroupedAllocations = () => {
    const groups = {};
    orgAllocations.forEach(alloc => {
      const clsName = normalizeClass(alloc.className);
      const cleanSec = normalizeSection(alloc.sectionName) || 'A';
      const key = `${clsName.toLowerCase()} - ${cleanSec.toLowerCase()}`;
      if (!groups[key]) {
        groups[key] = {
          className: clsName,
          section: cleanSec,
          allocations: []
        };
      }
      groups[key].allocations.push(alloc);
    });
    return Object.values(groups);
  };

  // Row Action Menu State
  const [activeMenuId, setActiveMenuId] = useState(null);

  // Class-Section Allocation Modal State
  const [showAllocationModal, setShowAllocationModal] = useState(false);
  const [savingAllocation, setSavingAllocation] = useState(false);
  const [isEditingAllocation, setIsEditingAllocation] = useState(false);
  const [editingAllocation, setEditingAllocation] = useState(null);
  const [allocationForm, setAllocationForm] = useState({
    className: '',
    section: 'Section A',
    subjectId: '',
    teacherId: '',
    teacherName: '',
    periodsPerWeek: 5
  });

  // Local subjects state with automatic backend fetch if props empty
  const [localSubjects, setLocalSubjects] = useState([]);
  const [loadingSubjects, setLoadingSubjects] = useState(false);

  useEffect(() => {
    const fetchSubjects = async () => {
      if (subjects && subjects.length > 0) {
        setLocalSubjects(subjects.filter(Boolean));
        return;
      }
      try {
        setLoadingSubjects(true);
        const res = await subjectService.getSubjectsByOrg('SCH-02');
        if (Array.isArray(res)) {
          setLocalSubjects(res.filter(Boolean));
          if (typeof setSubjects === 'function') {
            setSubjects(res.filter(Boolean));
          }
        }
      } catch (err) {
        console.error('Failed to load subjects:', err);
      } finally {
        setLoadingSubjects(false);
      }
    };
    fetchSubjects();
  }, [subjects, setSubjects]);

  // Real subjects list (prop or fetched) — always filter nulls
  const displaySubjects = ((subjects && subjects.length > 0) ? subjects : localSubjects).filter(Boolean);

  // Extract REAL teachers from database via props (no mock fallback!)
  const realTeachers = useMemo(() => {
    if (!teachers || !Array.isArray(teachers)) return [];
    return teachers
      .filter(t => t && t.status !== 'INACTIVE')
      .map(t => ({
        id: t.teacherId || t.id || t.code,
        name: t.teacherName || t.name || t.fullName
      }))
      .filter(t => Boolean(t.name));
  }, [teachers]);

  // Extract REAL classes from database via props (no mock fallback!)
  const realClasses = useMemo(() => {
    if (!classes || !Array.isArray(classes)) return [];
    return classes
      .map(c => ({
        id: c.classId || c.id,
        className: c.className || c.name || (c.classLevel ? `${c.classLevel}` : ''),
        section: c.section || 'A'
      }))
      .filter(c => Boolean(c.className));
  }, [classes]);

  // Derive unique target class / semester names based on institution type (for filter bar and analytics)
  const availableClassNames = useMemo(() => {
    if (isDegreeCollege) {
      const set = new Set();
      // Add standard semesters
      ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4', 'Sem 5', 'Sem 6', 'Sem 7', 'Sem 8'].forEach(s => set.add(s));

      // Add any distinct classLevels existing on current subjects
      displaySubjects.forEach(s => {
        if (s.classLevel && (s.classLevel.startsWith('Sem') || s.classLevel.includes('Sem'))) {
          set.add(s.classLevel);
        }
      });

      return Array.from(set);
    }

    if (isPuc) {
      const set = new Set([
        '1st PUC', '2nd PUC',
        'Science - 1st PUC', 'Science - 2nd PUC',
        'Commerce - 1st PUC', 'Commerce - 2nd PUC',
        'Arts - 1st PUC', 'Arts - 2nd PUC'
      ]);
      displaySubjects.forEach(s => { if (s.classLevel) set.add(s.classLevel); });
      return Array.from(set);
    }

    // Default for School
    const set = new Set();
    realClasses.forEach(c => set.add(c.className));
    if (set.size === 0) {
      displaySubjects.forEach(s => { if (s.classLevel) set.add(s.classLevel); });
    }
    if (set.size === 0) {
      ['Class 1', 'Class 2', 'Class 3', 'Class 4', 'Class 5', 'Class 6', 'Class 7', 'Class 8', 'Class 9', 'Class 10'].forEach(c => set.add(c));
    }
    return Array.from(set).sort();
  }, [isDegreeCollege, isPuc, realClasses, displaySubjects]);

  // Derive subject-specific target semesters for allocation modal (filters to subject's department/course)
  const modalTargetClassNames = useMemo(() => {
    if (!isDegreeCollege) {
      return availableClassNames;
    }

    const currentSub = displaySubjects.find(s => String(s.subjectId) === String(allocationForm.subjectId)) || selectedSubject;
    const prioritizedList = [];
    const seen = new Set();

    const addOption = (opt) => {
      if (opt && !seen.has(opt)) {
        seen.add(opt);
        prioritizedList.push(opt);
      }
    };

    if (currentSub) {
      // 1. If subject has a specific semester or classLevel, it is top priority!
      if (currentSub.semester) {
        const semStr = String(currentSub.semester).trim();
        const formatted = semStr.toLowerCase().startsWith('sem') ? semStr : `Sem ${semStr}`;
        addOption(formatted);
      }
      if (currentSub.classLevel) {
        addOption(currentSub.classLevel);
      }

      // 2. Department & Course matching for this subject
      const subDept = (currentSub.departmentName || currentSub.department || '').toLowerCase().trim();
      const subCourse = (currentSub.courseName || currentSub.courseCode || '').toLowerCase().trim();

      // Find programs that belong to this subject's department or course
      const matchingPrograms = (programsList || []).filter(p => {
        const pDept = (p.departmentName || (p.department && p.department.departmentName) || '').toLowerCase().trim();
        const pCode = (p.courseCode || '').toLowerCase().trim();
        const pName = (p.courseName || '').toLowerCase().trim();
        const pDeptId = p.departmentId || (p.department && p.department.departmentId);

        if (subCourse && (pCode === subCourse || pName === subCourse)) return true;
        if (subDept && (pDept === subDept || (pDeptId && currentSub.departmentId && String(pDeptId) === String(currentSub.departmentId)))) return true;
        if (subDept && (pCode.includes(subDept) || subDept.includes(pCode) || pName.includes(subDept) || subDept.includes(pName))) return true;
        return false;
      });

      if (matchingPrograms.length > 0) {
        matchingPrograms.forEach(p => {
          const code = p.courseCode || p.courseName;
          const sems = p.duration ? p.duration * 2 : 6;
          for (let i = 1; i <= sems; i++) {
            addOption(`${code} - Sem ${i}`);
          }
        });
      }

      // 3. Academic sections matching this subject's department or program
      if (academicSectionsList && academicSectionsList.length > 0) {
        academicSectionsList.forEach(s => {
          const sDept = (s.departmentName || '').toLowerCase().trim();
          const sProg = (s.programCode || s.programName || '').toLowerCase().trim();
          if ((subDept && sDept === subDept) || (subCourse && sProg === subCourse) || matchingPrograms.some(p => p.courseId === s.programId)) {
            if (s.programCode && s.semester) addOption(`${s.programCode} - ${s.semester}`);
            if (s.semester) addOption(s.semester);
          }
        });
      }
    }

    // Always include standard Sem 1 - Sem 8 so user can always allocate to standard semester
    ['Sem 1', 'Sem 2', 'Sem 3', 'Sem 4', 'Sem 5', 'Sem 6', 'Sem 7', 'Sem 8'].forEach(s => addOption(s));

    return prioritizedList;
  }, [isDegreeCollege, availableClassNames, allocationForm.subjectId, selectedSubject, displaySubjects, programsList, academicSectionsList]);

  // Derive available sections
  const availableSections = useMemo(() => {
    if (isDegreeCollege) {
      return ['Section A', 'Section B', 'Section C', 'Section D', 'Section E'];
    }
    if (isPuc) {
      return ['Section A', 'Section B', 'Section C', 'Section D'];
    }
    if (!allocationForm.className) return ['Section A', 'Section B', 'Section C', 'Section D'];
    const targetClassClean = normalizeClass(allocationForm.className).toLowerCase();
    const matchingSections = realClasses
      .filter(c => normalizeClass(c.className).toLowerCase() === targetClassClean)
      .map(c => {
        const sec = normalizeSection(c.section);
        return sec ? `Section ${sec}` : 'Section A';
      })
      .filter(Boolean);
    
    if (matchingSections.length > 0) {
      return Array.from(new Set(matchingSections)).sort();
    }
    return ['Section A', 'Section B', 'Section C', 'Section D'];
  }, [isDegreeCollege, isPuc, realClasses, allocationForm.className]);

  // Dynamic Analytics derived from real subjects
  const departmentData = useMemo(() => {
    const counts = {};
    (displaySubjects || []).forEach(sub => {
      const dept = sub.department || sub.departmentName || sub.type || 'General';
      counts[dept] = (counts[dept] || 0) + 1;
    });
    const colors = ['#3B82F6', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];
    return Object.keys(counts).map((name, idx) => ({
      name,
      value: counts[name],
      color: colors[idx % colors.length]
    }));
  }, [displaySubjects]);

  const classData = useMemo(() => {
    const counts = {};
    (displaySubjects || []).forEach(sub => {
      const cls = sub.classLevel || 'General';
      counts[cls] = (counts[cls] || 0) + 1;
    });
    return Object.keys(counts).map(name => ({
      name,
      subjects: counts[name]
    }));
  }, [displaySubjects]);

  // Faculty Teacher Allocation Counts
  const assignedCount = useMemo(() => {
    return displaySubjects.filter(s => Boolean(getSubjectTeacher(s))).length;
  }, [displaySubjects, orgAllocations]);

  const unassignedCount = useMemo(() => {
    return displaySubjects.length - assignedCount;
  }, [displaySubjects, assignedCount]);

  // Filtering Logic
  const filteredSubjects = useMemo(() => {
    return displaySubjects.filter(sub => {
      const query = search.toLowerCase().trim();
      const teacherName = getSubjectTeacher(sub);
      
      const matchesSearch = !query || 
                            (sub.subjectName || '').toLowerCase().includes(query) || 
                            (sub.subjectCode || '').toLowerCase().includes(query) ||
                            (sub.classLevel || '').toLowerCase().includes(query) ||
                            (sub.department || '').toLowerCase().includes(query) ||
                            teacherName.toLowerCase().includes(query) ||
                            (query === 'unassigned' && !teacherName);

      const matchesType = typeFilter === 'All' || sub.type === typeFilter;
      const matchesStatus = statusFilter === 'All' || (sub.status || 'ACTIVE').toUpperCase() === statusFilter.toUpperCase();
      const matchesClass = classFilter === 'All' || sub.classLevel === classFilter;

      return matchesSearch && matchesType && matchesStatus && matchesClass;
    });
  }, [displaySubjects, orgAllocations, search, typeFilter, statusFilter, classFilter]);

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedSubjects(filteredSubjects.map(s => s.subjectId));
    else setSelectedSubjects([]);
  };

  const handleSelect = (id) => {
    if (selectedSubjects.includes(id)) setSelectedSubjects(selectedSubjects.filter(s => s !== id));
    else setSelectedSubjects([...selectedSubjects, id]);
  };

  const getStatusStyle = (status) => {
    switch((status || 'ACTIVE').toUpperCase()) {
      case 'ACTIVE': return { bg: '#DCFCE7', color: '#166534', icon: <CheckCircle size={14}/>, label: '● Active' };
      case 'INACTIVE': return { bg: '#F1F5F9', color: '#475569', icon: <Clock size={14}/>, label: '● Inactive' };
      case 'DRAFT': return { bg: '#DBEAFE', color: '#1E40AF', icon: <Pencil size={14}/>, label: '● Draft' };
      default: return { bg: '#DCFCE7', color: '#166534', icon: <CheckCircle size={14}/>, label: '● Active' };
    }
  };

  // Fetch real persisted allocations from backend when opening drawer
  const openDrawer = async (subject) => {
    setSelectedSubject(subject);
    setDrawerOpen(true);
    setActiveMenuId(null);
    setLoadingAllocations(true);
    setDrawerAllocations([]);

    try {
      if (subject && subject.subjectId) {
        const res = await subjectService.getSubjectAllocations(subject.subjectId, '2026-2027');
        if (Array.isArray(res)) {
          setDrawerAllocations(res);
        } else if (res && Array.isArray(res.allocations)) {
          setDrawerAllocations(res.allocations);
        }
      }
    } catch (err) {
      console.error('Error fetching subject allocations:', err);
    } finally {
      setLoadingAllocations(false);
    }
  };

  const openAddAllocation = (className = '', section = '', targetSubject = null) => {
    setIsEditingAllocation(false);
    setEditingAllocation(null);

    const defaultSubject = targetSubject || (displaySubjects.length > 0 ? displaySubjects[0] : null);
    setSelectedSubject(defaultSubject);

    // Auto-detect semester from targetSubject
    let defaultClass = className;
    if (!defaultClass && defaultSubject) {
      if (defaultSubject.semester) {
        const s = String(defaultSubject.semester).trim();
        defaultClass = s.toLowerCase().startsWith('sem') ? s : `Sem ${s}`;
      } else if (defaultSubject.classLevel) {
        defaultClass = defaultSubject.classLevel;
      }
    }

    if (!defaultClass) {
      defaultClass = isDegreeCollege ? 'Sem 1' : (isPuc ? '1st PUC' : 'Class 1');
    }

    const cleanSec = normalizeSection(section) || 'A';
    const defaultSection = `Section ${cleanSec}`;

    setAllocationForm({
      className: defaultClass,
      section: defaultSection,
      subjectId: defaultSubject ? String(defaultSubject.subjectId) : '',
      teacherId: '',
      teacherName: '',
      periodsPerWeek: 5
    });
    setShowAllocationModal(true);
  };

  const openEditAllocation = (allocation) => {
    setIsEditingAllocation(true);
    setEditingAllocation(allocation);

    const associatedSubject = displaySubjects.find(s => String(s.subjectId) === String(allocation.subjectId));
    setSelectedSubject(associatedSubject || { subjectId: allocation.subjectId, subjectName: allocation.subjectName });

    const cleanSec = normalizeSection(allocation.sectionName) || 'A';
    setAllocationForm({
      className: normalizeClass(allocation.className),
      section: `Section ${cleanSec}`,
      subjectId: String(allocation.subjectId),
      teacherId: allocation.teacherId ? String(allocation.teacherId) : '',
      teacherName: allocation.teacherName || '',
      periodsPerWeek: allocation.periodsPerWeek
    });
    setShowAllocationModal(true);
  };

  const handleCloseAllocationModal = () => {
    setShowAllocationModal(false);
    setIsEditingAllocation(false);
    setEditingAllocation(null);
    setAllocationForm({
      className: '',
      section: 'Section A',
      subjectId: '',
      teacherId: '',
      teacherName: '',
      periodsPerWeek: 5
    });
  };

  // Save/Update allocation to backend database
  const handleSaveClassAllocation = async () => {
    const periodsVal = parseInt(allocationForm.periodsPerWeek, 10);
    if (isNaN(periodsVal) || periodsVal <= 0 || periodsVal > 40) {
      alert('Periods per week must be a valid positive number (between 1 and 40).');
      return;
    }

    const targetSubId = isEditingAllocation ? editingAllocation.subjectId : parseInt(allocationForm.subjectId, 10);
    if (!targetSubId) {
      alert('Please select a valid subject.');
      return;
    }

    const cleanFormClass = normalizeClass(allocationForm.className);
    const cleanFormSec = normalizeSection(allocationForm.section) || 'A';

    if (!isEditingAllocation) {
      if (!cleanFormClass || !cleanFormSec) {
        alert('Please select a Class and Section.');
        return;
      }

      // Prevent duplicate allocation for same Organization + Academic Year + Class + Section + Subject
      const duplicate = orgAllocations.some(a => 
        String(a.subjectId) === String(targetSubId) &&
        normalizeClass(a.className).toLowerCase() === cleanFormClass.toLowerCase() &&
        normalizeSection(a.sectionName).toLowerCase() === cleanFormSec.toLowerCase()
      );
      if (duplicate) {
        alert('This subject is already allocated to this class and section.');
        return;
      }
    }

    // Resolve teacher ID and Name
    let resolvedTeacherId = allocationForm.teacherId || '';
    let resolvedTeacherName = allocationForm.teacherName || '';
    
    if (resolvedTeacherId) {
      const match = realTeachers.find(t => String(t.id || t.teacherId) === String(resolvedTeacherId));
      if (match) resolvedTeacherName = match.name || match.teacherName || '';
    } else if (resolvedTeacherName) {
      const match = realTeachers.find(t => t.name === resolvedTeacherName);
      if (match) resolvedTeacherId = match.id || match.teacherId || '';
    }

    try {
      setSavingAllocation(true);

      const targetCls = normalizeClass(isEditingAllocation ? editingAllocation.className : cleanFormClass);
      const targetSec = normalizeSection(isEditingAllocation ? editingAllocation.sectionName : cleanFormSec) || 'A';

      const payload = {
        subjectId: targetSubId,
        className: targetCls,
        sectionName: targetSec,
        teacherId: resolvedTeacherId,
        teacherName: resolvedTeacherName || 'Not Assigned',
        periodsPerWeek: periodsVal,
        academicYear: '2026-2027'
      };

      if (isEditingAllocation && editingAllocation) {
        payload.id = editingAllocation.id;
      }

      await subjectService.createSubjectAllocation(payload);
      
      // Update subject primary teacher if unassigned
      if (onAssignTeacher && resolvedTeacherName) {
        const sub = displaySubjects.find(s => String(s.subjectId) === String(payload.subjectId));
        if (sub) {
          await onAssignTeacher(sub, resolvedTeacherName);
        }
      }

      handleCloseAllocationModal();
      fetchOrgAllocations();
    } catch (err) {
      console.error('Failed to save allocation:', err);
      alert('Failed to save allocation: ' + (err.message || 'Error occurred'));
    } finally {
      setSavingAllocation(false);
    }
  };

  // Group real drawer allocations by Class Name
  const groupedAllocations = useMemo(() => {
    const grouped = {};
    drawerAllocations.forEach(item => {
      const cls = item.className || item.classLevel || 'General';
      if (!grouped[cls]) grouped[cls] = [];
      grouped[cls].push(item);
    });
    return grouped;
  }, [drawerAllocations]);

  return (
    <div style={styles.container} onClick={() => setActiveMenuId(null)}>
      {/* HEADER */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            <span style={{ backgroundColor: '#DBEAFE', padding: '10px', borderRadius: '12px', color: '#2563EB', display: 'flex' }}>
              <BookOpen size={26} />
            </span>
            Subject Management
          </h1>
          <p style={styles.subtitle}>Manage subjects master directory, curriculum structure, and class-section faculty allocations.</p>
        </div>
        <div style={styles.headerActions}>
          <button style={styles.btnSecondary} onClick={() => alert('Exporting subject data...')}>
            <Download size={18} /> Export Excel
          </button>
          <button style={styles.btnSecondary} onClick={() => alert('Import subject template...')}>
            <Upload size={18} /> Import
          </button>
          <button style={styles.btnPrimary} onClick={onAddSubject}>
            <Plus size={18} /> Add Subject
          </button>
        </div>
      </div>

      {/* SUB-TABS NAVIGATION */}
      <div style={{
        display: 'flex',
        gap: '12px',
        marginBottom: '20px',
        borderBottom: '2px solid #E2E8F0',
        paddingBottom: '2px'
      }}>
        <button
          onClick={() => setActiveSubjectTab('master')}
          style={{
            padding: '10px 18px',
            border: 'none',
            background: 'none',
            borderBottom: activeSubjectTab === 'master' ? '3px solid #2563EB' : '3px solid transparent',
            color: activeSubjectTab === 'master' ? '#2563EB' : '#64748B',
            fontWeight: activeSubjectTab === 'master' ? '700' : '600',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <BookOpen size={16} /> Subject Master Directory
        </button>
        <button
          onClick={() => setActiveSubjectTab('allocations')}
          style={{
            padding: '10px 18px',
            border: 'none',
            background: 'none',
            borderBottom: activeSubjectTab === 'allocations' ? '3px solid #2563EB' : '3px solid transparent',
            color: activeSubjectTab === 'allocations' ? '#2563EB' : '#64748B',
            fontWeight: activeSubjectTab === 'allocations' ? '700' : '600',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          <School size={16} /> {isDegreeCollege ? 'Semester / Section Subject Allocations' : isPuc ? 'Stream / Section Subject Allocations' : 'Class / Section Subject Allocations'}
        </button>
      </div>

      {/* MASTER TAB CONTENT */}
      {activeSubjectTab === 'master' && (
        <>
          {/* KPI CARDS */}
      <div style={styles.gridCards}>
        {[
          { title: 'Total Subjects', value: displaySubjects.length, icon: BookOpen, color: '#3B82F6', bg: '#EFF6FF' },
          { title: 'Core Subjects', value: displaySubjects.filter(s => (s.type || 'Core') === 'Core').length, icon: GraduationCap, color: '#10B981', bg: '#ECFDF5' },
          { title: 'Elective Subjects', value: displaySubjects.filter(s => s.type === 'Elective').length, icon: Notebook, color: '#F59E0B', bg: '#FFFBEB' },
          { title: isDegreeCollege ? 'Assigned Faculty' : isPuc ? 'Assigned Lecturers' : 'Assigned Teachers', value: assignedCount, icon: Users, color: '#8B5CF6', bg: '#F5F3FF' },
        ].map((kpi, idx) => (
          <div key={idx} style={styles.card} className="hover-card">
            <div style={{...styles.iconBox, backgroundColor: kpi.bg, color: kpi.color}}>
              <kpi.icon size={22} />
            </div>
            <h2 style={styles.cardNumber}>{kpi.value}</h2>
            <p style={styles.cardTitle}>{kpi.title}</p>
          </div>
        ))}
      </div>

      {/* SEARCH & FILTERS BAR */}
      <div style={styles.filtersContainer}>
        <div style={{ position: 'relative' }}>
          <Search size={18} color="#94A3B8" style={{ position: 'absolute', left: '14px', top: '13px' }} />
          <input 
            type="text" 
            placeholder={isDegreeCollege ? "Search Subject Name, Code, Semester..." : isPuc ? "Search Subject Name, Code, Stream..." : "Search Subject Name, Code, Class..."} 
            style={styles.searchInput}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select style={styles.select} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
          <option value="All">All Types</option>
          <option value="Core">Core</option>
          <option value="Elective">Elective</option>
          <option value="Optional">Optional</option>
        </select>
        <select style={styles.select} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          <option value="All">All Statuses</option>
          <option value="ACTIVE">Active</option>
          <option value="INACTIVE">Inactive</option>
          <option value="DRAFT">Draft</option>
        </select>
        {availableClassNames.length > 0 && (
          <select style={styles.select} value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
            <option value="All">{isDegreeCollege ? 'All Semesters' : isPuc ? 'All Streams / Years' : 'All Classes / Grades'}</option>
            {availableClassNames.map(cls => (
              <option key={cls} value={cls}>{cls}</option>
            ))}
          </select>
        )}

        {selectedSubjects.length > 0 && (
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span style={{ fontSize: '13.5px', fontWeight: '700', color: '#2563EB', marginRight: '4px' }}>
              ✓ {selectedSubjects.length} selected
            </span>
            <button style={{...styles.btnSecondary, padding: '8px 14px', fontSize: '12.5px'}}>
              Deactivate
            </button>
            <button 
              style={{...styles.btnSecondary, color: '#EF4444', borderColor: '#FEE2E2', backgroundColor: '#FEF2F2', padding: '8px 14px', fontSize: '12.5px'}}
              onClick={() => {
                if (window.confirm(`Are you sure you want to delete ${selectedSubjects.length} selected subject(s)?`)) {
                  setSelectedSubjects([]);
                }
              }}
            >
              <Trash size={15}/> Delete Selected
            </button>
          </div>
        )}
      </div>

      {/* SUBJECT DIRECTORY TABLE */}
      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={{...styles.th, width: '44px'}}>
                <input 
                  type="checkbox" 
                  onChange={handleSelectAll} 
                  checked={selectedSubjects.length === filteredSubjects.length && filteredSubjects.length > 0} 
                />
              </th>
              <th style={styles.th}>Subject</th>
              <th style={styles.th}>Code</th>
              <th style={styles.th}>{isDegreeCollege ? 'Semester' : isPuc ? 'Stream / Year' : 'Class / Grade'}</th>
              <th style={styles.th}>Department</th>
              <th style={styles.th}>{isDegreeCollege ? 'Faculty Teacher' : isPuc ? 'Lecturer' : 'Teacher'}</th>
              <th style={styles.th}>Marks (T/P)</th>
              <th style={styles.th}>Status</th>
              <th style={{...styles.th, textAlign: 'right'}}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredSubjects.length > 0 ? filteredSubjects.map(sub => {
              const statusStyle = getStatusStyle(sub.status);
              const teacherName = getSubjectTeacher(sub);

              return (
                <tr 
                  key={sub.subjectId} 
                  style={{ 
                    backgroundColor: selectedSubjects.includes(sub.subjectId) ? '#EFF6FF' : 'white', 
                    transition: 'background-color 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => openDrawer(sub)}
                >
                  <td style={styles.td} onClick={(e) => e.stopPropagation()}>
                    <input 
                      type="checkbox" 
                      checked={selectedSubjects.includes(sub.subjectId)} 
                      onChange={() => handleSelect(sub.subjectId)} 
                    />
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: '700', fontSize: '15px', color: '#0F172A' }}>{sub.subjectName}</div>
                    <div style={{ fontSize: '12.5px', color: '#64748B', marginTop: '3px' }}>
                      {sub.shortName || sub.subjectCode} • <span style={{ fontWeight: '600', color: '#2563EB' }}>{sub.type || 'Core'}</span>
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{ fontFamily: 'monospace', backgroundColor: '#F1F5F9', color: '#334155', padding: '4px 10px', borderRadius: '6px', fontSize: '13px', fontWeight: '700' }}>
                      {sub.subjectCode}
                    </span>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontWeight: '600', color: '#0F172A', fontSize: '13.5px' }}>{sub.classLevel || 'General'}</div>
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: '13.5px', color: '#475569', fontWeight: '500' }}>
                      {sub.department || sub.departmentName || 'General Academic'}
                    </div>
                  </td>
                  <td style={styles.td}>
                    {teacherName ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ width: '30px', height: '30px', borderRadius: '50%', backgroundColor: '#DBEAFE', color: '#1D4ED8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700' }}>
                          {teacherName.charAt(0).toUpperCase()}
                        </div>
                        <span style={{ fontSize: '13.5px', fontWeight: '600', color: '#0F172A' }}>{teacherName}</span>
                      </div>
                    ) : (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          openAddAllocation('', '', sub);
                        }}
                        style={{
                          backgroundColor: '#FEF3C7',
                          color: '#B45309',
                          border: '1px solid #FDE68A',
                          padding: '5px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '700',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        ⚠️ Assign Teacher
                      </button>
                    )}
                  </td>
                  <td style={styles.td}>
                    <div style={{ fontSize: '13.5px', color: '#334155', fontWeight: '600' }}>
                      {sub.theoryMarks || 100} / {sub.practicalMarks || 0}
                    </div>
                    <div style={{ fontSize: '11px', color: '#94A3B8', marginTop: '2px' }}>
                      Max: {sub.maxMarks || (parseInt(sub.theoryMarks || 100) + parseInt(sub.practicalMarks || 0))}
                    </div>
                  </td>
                  <td style={styles.td}>
                    <span style={{...styles.badge, backgroundColor: statusStyle.bg, color: statusStyle.color}}>
                      {statusStyle.label}
                    </span>
                  </td>
                  <td style={{...styles.td, textAlign: 'right', position: 'relative'}} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '6px' }}>
                      <button 
                        onClick={() => openDrawer(sub)} 
                        style={{ padding: '8px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', cursor: 'pointer', color: '#64748B' }} 
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button 
                        onClick={() => onEdit(sub)} 
                        style={{ padding: '8px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '6px', cursor: 'pointer', color: '#2563EB' }} 
                        title="Edit"
                      >
                        <Pencil size={16} />
                      </button>
                      <button 
                        onClick={() => setActiveMenuId(activeMenuId === sub.subjectId ? null : sub.subjectId)} 
                        style={{ padding: '8px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '6px', cursor: 'pointer', color: '#475569' }} 
                        title="More Actions"
                      >
                        <MoreVertical size={16} />
                      </button>

                      {/* Dropdown Action Menu */}
                      {activeMenuId === sub.subjectId && (
                        <div 
                          style={{
                            position: 'absolute',
                            right: '18px',
                            top: '48px',
                            backgroundColor: 'white',
                            borderRadius: '10px',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.12)',
                            border: '1px solid #E2E8F0',
                            padding: '6px',
                            zIndex: 50,
                            minWidth: '170px',
                            textAlign: 'left'
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => {
                              openDrawer(sub);
                              setActiveMenuId(null);
                            }}
                            style={{ width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', textAlign: 'left', fontSize: '13px', cursor: 'pointer', color: '#334155', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F1F5F9'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <Eye size={15} color="#64748B"/> View Profile
                          </button>

                          <button
                            onClick={() => {
                              onEdit(sub);
                              setActiveMenuId(null);
                            }}
                            style={{ width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', textAlign: 'left', fontSize: '13px', cursor: 'pointer', color: '#334155', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F1F5F9'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <Pencil size={15} color="#2563EB"/> Edit Subject
                          </button>

                          <button
                            onClick={() => {
                              openAddAllocation('', '', sub);
                              setActiveMenuId(null);
                            }}
                            style={{ width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', textAlign: 'left', fontSize: '13px', cursor: 'pointer', color: '#334155', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#F1F5F9'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <Users size={15} color="#8B5CF6"/> Assign Allocations
                          </button>

                          <div style={{ height: '1px', backgroundColor: '#E2E8F0', margin: '4px 0' }} />

                          <button
                            onClick={() => {
                              if (onDeleteSubject) onDeleteSubject(sub);
                              setActiveMenuId(null);
                            }}
                            style={{ width: '100%', padding: '8px 12px', background: 'transparent', border: 'none', textAlign: 'left', fontSize: '13px', cursor: 'pointer', color: '#DC2626', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '8px' }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#FEF2F2'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                          >
                            <Trash size={15} color="#DC2626"/> Delete Subject
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan="9" style={{ padding: '64px', textAlign: 'center' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
                    <div style={{ width: '64px', height: '64px', borderRadius: '50%', backgroundColor: '#F1F5F9', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94A3B8' }}>
                      <BookOpen size={32} />
                    </div>
                    <div>
                      <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#0F172A' }}>No subjects found</h3>
                      <p style={{ margin: 0, color: '#64748B' }}>Get started by creating your first subject or importing from Excel.</p>
                    </div>
                    <button style={{...styles.btnPrimary, marginTop: '8px'}} onClick={onAddSubject}>
                      <Plus size={18} /> Add First Subject
                    </button>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* BOTTOM ANALYTICS & TEACHER ALLOCATION METRICS */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px' }}>
        {/* Analytics Card 1: Subjects by Department */}
        <div style={{...styles.card, padding: '24px'}}>
          <h3 style={{ fontSize: '15px', fontWeight: '700', margin: '0 0 20px 0', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Building size={18} color="#3B82F6"/> Subjects by Department
          </h3>
          <div style={{ height: '220px' }}>
            {departmentData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={departmentData} cx="50%" cy="50%" innerRadius={55} outerRadius={75} paddingAngle={4} dataKey="value">
                    {departmentData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Legend iconType="circle" wrapperStyle={{ fontSize: '12px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '13px' }}>
                No department data available
              </div>
            )}
          </div>
        </div>

        {/* Analytics Card 2: Subjects by Class / Semester */}
        <div style={{...styles.card, padding: '24px'}}>
          <h3 style={{ fontSize: '15px', fontWeight: '700', margin: '0 0 20px 0', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <School size={18} color="#10B981"/> {isDegreeCollege ? 'Subjects by Semester' : isPuc ? 'Subjects by Stream / Year' : 'Subjects by Class'}
          </h3>
          <div style={{ height: '220px' }}>
            {classData.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={classData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
                  <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#64748B', fontSize: 12}} />
                  <YAxis axisLine={false} tickLine={false} tick={{fill: '#64748B', fontSize: 12}} />
                  <RechartsTooltip cursor={{fill: '#F1F5F9'}} contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }} />
                  <Bar dataKey="subjects" fill="#2563EB" radius={[4, 4, 0, 0]} barSize={28} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#94a3b8', fontSize: '13px' }}>
                No {isDegreeCollege ? 'semester' : isPuc ? 'stream' : 'class'} level distribution available
              </div>
            )}
          </div>
        </div>

        {/* Analytics Card 3: Actionable Teacher Allocation Status */}
        <div style={{...styles.card, padding: '24px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between'}}>
          <div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', margin: '0 0 16px 0', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Users size={18} color="#8B5CF6"/> {isDegreeCollege ? 'Faculty Allocation Status' : isPuc ? 'Lecturer Allocation Status' : 'Teacher Allocation Status'}
            </h3>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div style={{ background: '#F8FAFC', padding: '12px 14px', borderRadius: '10px', border: '1px solid #E2E8F0' }}>
                <div style={{ fontSize: '12px', color: '#64748B', fontWeight: 600 }}>Assigned Subjects</div>
                <div style={{ fontSize: '22px', fontWeight: 800, color: '#166534', marginTop: '2px' }}>
                  {assignedCount}
                </div>
              </div>
              <div style={{ background: unassignedCount > 0 ? '#FEF2F2' : '#F8FAFC', padding: '12px 14px', borderRadius: '10px', border: unassignedCount > 0 ? '1px solid #FCA5A5' : '1px solid #E2E8F0' }}>
                <div style={{ fontSize: '12px', color: unassignedCount > 0 ? '#991B1B' : '#64748B', fontWeight: 600 }}>Unassigned Subjects</div>
                <div style={{ fontSize: '22px', fontWeight: 800, color: unassignedCount > 0 ? '#DC2626' : '#0F172A', marginTop: '2px' }}>
                  {unassignedCount}
                </div>
              </div>
            </div>

            {unassignedCount > 0 ? (
              <div style={{ padding: '10px 14px', background: '#FFF7ED', border: '1px solid #FFEDD5', borderRadius: '8px', fontSize: '12.5px', color: '#C2410C', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px' }}>⚠️</span>
                <span>{unassignedCount} {unassignedCount === 1 ? 'subject requires' : 'subjects require'} {isDegreeCollege ? 'faculty teacher' : isPuc ? 'lecturer' : 'teacher'} allocation</span>
              </div>
            ) : (
              <div style={{ padding: '10px 14px', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: '8px', fontSize: '12.5px', color: '#15803D', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '14px' }}>✅</span>
                <span>All subjects have assigned {isDegreeCollege ? 'faculty teachers' : isPuc ? 'lecturers' : 'teachers'}</span>
              </div>
            )}
          </div>

          <button 
            style={{ ...styles.btnSecondary, width: '100%', justifyContent: 'center', marginTop: '16px', fontSize: '13px' }}
            onClick={() => setSearch(unassignedCount > 0 ? 'unassigned' : '')}
          >
            {unassignedCount > 0 ? '🔍 View Unassigned Subjects' : 'View All Allocations'}
          </button>
        </div>
      </div>
      </>
      )}

      {/* CLASS / SEMESTER SUBJECT ALLOCATIONS TAB */}
      {activeSubjectTab === 'allocations' && (
        <div style={{ background: 'white', borderRadius: '14px', padding: '24px', border: '1px solid #E2E8F0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #E2E8F0', paddingBottom: '16px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '800', color: '#0F172A' }}>
                {isDegreeCollege ? 'Semester & Section Subject Allocations' : isPuc ? 'Stream & Section Subject Allocations' : 'Class & Section Subject Allocations'}
              </h3>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#64748B' }}>
                Academic configuration linking {isDegreeCollege ? 'semesters' : isPuc ? 'streams' : 'classes'}, sections, subjects, assigned {isDegreeCollege ? 'faculty teachers' : isPuc ? 'lecturers' : 'teachers'}, and weekly period limits.
              </p>
            </div>
            <button
              onClick={() => {
                if (displaySubjects.length > 0) {
                  setSelectedSubject(displaySubjects[0]);
                  setShowAllocationModal(true);
                } else {
                  alert('Please add a subject first.');
                }
              }}
              style={{
                backgroundColor: '#2563EB',
                color: 'white',
                border: 'none',
                padding: '9px 16px',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '700',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <Plus size={16} /> Assign Subject to {isDegreeCollege ? 'Semester' : isPuc ? 'Stream' : 'Class'}
            </button>
          </div>

          {loadingOrgAllocations ? (
            <div style={{ padding: '40px', textAlign: 'center', fontSize: '14px', color: '#64748B' }}>
              🔄 Loading subject allocations from database...
            </div>
          ) : getGroupedAllocations().length === 0 ? (
            <div style={{ padding: '60px', textAlign: 'center', background: '#F8FAFC', borderRadius: '12px', border: '1px dashed #CBD5E1' }}>
              <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#64748B' }}>No subject allocations scheduled.</p>
              <p style={{ margin: '4px 0 0 0', fontSize: '12.5px', color: '#94A3B8' }}>Click "Assign Subject to {isDegreeCollege ? 'Semester' : isPuc ? 'Stream' : 'Class'}" to schedule subjects and assign {isDegreeCollege ? 'faculty teachers' : isPuc ? 'lecturers' : 'teachers'}.</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '18px' }}>
              {getGroupedAllocations().map((cls, idx) => (
                <div key={idx} style={{ background: '#F8FAFC', borderRadius: '12px', border: '1px solid #CBD5E1', padding: '16px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', borderBottom: '1px solid #E2E8F0', paddingBottom: '10px' }}>
                    <div style={{ fontSize: '15px', fontWeight: '800', color: '#0F172A', display: 'flex', alignItems: 'center', gap: '6px' }}>
                      {isDegreeCollege ? '🎓' : isPuc ? '🏛️' : '🏫'} {cls.className} <span style={{ color: '#2563EB' }}>Section {cls.section}</span>
                      <button
                        onClick={() => handleDeleteClassAllocations(cls.className, cls.section)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#EF4444',
                          cursor: 'pointer',
                          padding: '2px',
                          display: 'inline-flex',
                          alignItems: 'center'
                        }}
                        title={`Delete All Allocations for this ${isDegreeCollege ? 'Semester' : isPuc ? 'Stream' : 'Class'} & Section`}
                      >
                        <Trash size={15} />
                      </button>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '11.5px', background: '#DBEAFE', color: '#1E40AF', padding: '3px 10px', borderRadius: '12px', fontWeight: '700' }}>
                        {cls.allocations.length} Subjects
                      </span>
                      <button
                        onClick={() => openAddAllocation(cls.className, cls.section)}
                        style={{
                          background: '#EFF6FF',
                          border: 'none',
                          color: '#2563EB',
                          cursor: 'pointer',
                          fontSize: '11.5px',
                          fontWeight: '700',
                          padding: '3px 8px',
                          borderRadius: '6px',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '3px'
                        }}
                        title={`Add Subject to ${isDegreeCollege ? 'Semester' : isPuc ? 'Stream' : 'Class'}`}
                      >
                        <Plus size={11} /> Add
                      </button>
                    </div>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {cls.allocations.map((item, i) => (
                      <div key={i} style={{ background: 'white', borderRadius: '8px', padding: '12px', border: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#0F172A' }}>{item.subjectName}</div>
                          <div style={{ fontSize: '12px', color: '#64748B', marginTop: '2px' }}>{isDegreeCollege ? 'Faculty' : isPuc ? 'Lecturer' : 'Teacher'}: <strong style={{ color: '#334155' }}>{item.teacherName}</strong></div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <span style={{ fontSize: '11.5px', color: '#166534', fontWeight: '700', background: '#DCFCE7', padding: '3px 8px', borderRadius: '6px', marginRight: '4px' }}>
                            {item.periodsPerWeek} p/w
                          </span>
                          <button
                            onClick={() => openEditAllocation(item)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#2563EB',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Edit Allocation"
                          >
                            <Pencil size={13} />
                          </button>
                          <button
                            onClick={() => handleDeleteAllocation(item.id)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#EF4444',
                              cursor: 'pointer',
                              padding: '4px',
                              display: 'flex',
                              alignItems: 'center'
                            }}
                            title="Delete Allocation"
                          >
                            <Trash size={13} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* OVERLAY & REAL PERSISTED SUBJECT PROFILE DRAWER */}
      <div style={{...styles.overlay, ...(drawerOpen ? styles.overlayOpen : {})}} onClick={() => setDrawerOpen(false)} />
      <div style={{...styles.drawer, ...(drawerOpen ? styles.drawerOpen : {})}}>
        {selectedSubject && (() => {
          const statusStyle = getStatusStyle(selectedSubject.status);

          return (
            <>
              {/* DRAWER HEADER */}
              <div style={{ padding: '24px', borderBottom: '1px solid #E2E8F0', backgroundColor: '#F8FAFC', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h2 style={{ margin: '0 0 6px 0', fontSize: '22px', fontWeight: '800', color: '#0F172A' }}>
                    {selectedSubject.subjectName}
                  </h2>
                  
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '10px' }}>
                    <span style={{ fontFamily: 'monospace', backgroundColor: '#E2E8F0', color: '#1E293B', padding: '3px 8px', borderRadius: '6px', fontSize: '12.5px', fontWeight: '700' }}>
                      {selectedSubject.subjectCode}
                    </span>
                    <span style={{ fontSize: '13.5px', color: '#64748B', fontWeight: '500' }}>
                      {selectedSubject.department || selectedSubject.departmentName || 'Languages'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{...styles.badge, backgroundColor: statusStyle.bg, color: statusStyle.color}}>
                      {statusStyle.label}
                    </span>
                    <span style={{ backgroundColor: '#DBEAFE', color: '#1D4ED8', padding: '4px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '700' }}>
                      {selectedSubject.type || 'Core'}
                    </span>
                  </div>
                </div>

                <button 
                  onClick={() => setDrawerOpen(false)} 
                  style={{ background: 'white', border: '1px solid #CBD5E1', cursor: 'pointer', padding: '8px', borderRadius: '50%', color: '#64748B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                >
                  <X size={18} />
                </button>
              </div>

              {/* DRAWER BODY — REAL ALLOCATION SECTIONS */}
              <div style={{ padding: '24px', overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: '24px' }}>
                
                {/* SECTION 1: SUBJECT MASTER ACADEMIC INFORMATION */}
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '14px' }}>
                    ACADEMIC INFORMATION (SUBJECT MASTER)
                  </div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '16px' }}>
                    <div>
                      <div style={{ fontSize: '12px', color: '#64748B', fontWeight: '600', marginBottom: '4px' }}>Subject Code</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A', fontFamily: 'monospace' }}>
                        {selectedSubject.subjectCode}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', color: '#64748B', fontWeight: '600', marginBottom: '4px' }}>Department</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>
                        {selectedSubject.department || selectedSubject.departmentName || 'Languages'}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', color: '#64748B', fontWeight: '600', marginBottom: '4px' }}>Subject Type</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#2563EB' }}>
                        {selectedSubject.type || 'Core'}
                      </div>
                    </div>

                    <div>
                      <div style={{ fontSize: '12px', color: '#64748B', fontWeight: '600', marginBottom: '4px' }}>Status</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#166534' }}>
                        {selectedSubject.status || 'ACTIVE'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* SECTION 2: REAL CLASS / SEMESTER & SECTION ALLOCATION */}
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{isDegreeCollege ? 'SEMESTER & SECTION ALLOCATION' : isPuc ? 'STREAM & SECTION ALLOCATION' : 'CLASS & SECTION ALLOCATION'}</span>
                    <button 
                      onClick={() => openAddAllocation('', '', selectedSubject)}
                      style={{ background: '#EFF6FF', border: '1px solid #BFDBFE', color: '#2563EB', padding: '5px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px' }}
                    >
                      <Plus size={14} /> Add Allocation
                    </button>
                  </div>

                  {loadingAllocations ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: '#64748B', fontSize: '13px' }}>
                      Loading persisted allocations...
                    </div>
                  ) : Object.keys(groupedAllocations).length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                      {Object.keys(groupedAllocations).map(className => (
                        <div key={className} style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '16px' }}>
                          <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#0F172A', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <School size={16} color="#2563EB"/> {className}
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {groupedAllocations[className].map(alloc => (
                              <div key={alloc.id || `${alloc.className}-${alloc.sectionName}`} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px' }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                  <span style={{ fontWeight: '800', fontSize: '13px', color: '#2563EB', backgroundColor: '#DBEAFE', width: '26px', height: '26px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    {(alloc.sectionName || alloc.section || 'A').replace('Section ', '')}
                                  </span>
                                  <div>
                                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>{alloc.teacherName}</div>
                                    <div style={{ fontSize: '12px', color: '#64748B' }}>{isDegreeCollege ? 'Faculty Teacher' : isPuc ? 'Lecturer' : 'Teacher'}</div>
                                  </div>
                                </div>

                                <div style={{ fontSize: '13px', fontWeight: '700', color: '#166534', backgroundColor: '#DCFCE7', padding: '4px 10px', borderRadius: '12px' }}>
                                  {alloc.periodsPerWeek} periods/week
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    /* EXACT SPECIFIED EMPTY STATE FOR UNCONFIGURED SUBJECTS */
                    <div style={{ backgroundColor: '#F8FAFC', border: '1px dashed #CBD5E1', borderRadius: '12px', padding: '24px', textAlign: 'center' }}>
                      <div style={{ fontSize: '13.5px', color: '#64748B', fontWeight: '500', marginBottom: '14px' }}>
                        No {isDegreeCollege ? 'semester' : isPuc ? 'stream' : 'class'} or section allocations configured yet.
                      </div>
                      <button 
                        onClick={() => openAddAllocation('', '', selectedSubject)}
                        style={{ ...styles.btnPrimary, margin: '0 auto', fontSize: '13px' }}
                      >
                        <Plus size={16} /> Add Allocation
                      </button>
                    </div>
                  )}
                </div>

                {/* SECTION 3: MARKS CONFIGURATION */}
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '700', color: '#2563EB', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '14px' }}>
                    MARKS CONFIGURATION
                  </div>

                  <div style={{ backgroundColor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '12px', padding: '18px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px dashed #CBD5E1' }}>
                      <span style={{ fontSize: '13.5px', color: '#475569', fontWeight: '500' }}>Theory Marks</span>
                      <span style={{ fontSize: '14.5px', fontWeight: '700', color: '#0F172A' }}>{selectedSubject.theoryMarks || 100}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px dashed #CBD5E1' }}>
                      <span style={{ fontSize: '13.5px', color: '#475569', fontWeight: '500' }}>Practical Marks</span>
                      <span style={{ fontSize: '14.5px', fontWeight: '700', color: '#0F172A' }}>{selectedSubject.practicalMarks || 0}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '4px' }}>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#0F172A' }}>Maximum Marks</span>
                      <span style={{ fontSize: '17px', fontWeight: '800', color: '#2563EB' }}>
                        {selectedSubject.maxMarks || (parseInt(selectedSubject.theoryMarks || 100) + parseInt(selectedSubject.practicalMarks || 0))}
                      </span>
                    </div>
                  </div>
                </div>

              </div>

              {/* DRAWER FOOTER */}
              <div style={{ padding: '20px 24px', borderTop: '1px solid #E2E8F0', display: 'flex', justifyContent: 'flex-end', gap: '12px', backgroundColor: '#F8FAFC' }}>
                <button onClick={() => setDrawerOpen(false)} style={{...styles.btnSecondary, padding: '10px 20px'}}>
                  Close
                </button>
                <button 
                  onClick={() => {
                    setDrawerOpen(false);
                    onEdit(selectedSubject);
                  }} 
                  style={{...styles.btnPrimary, padding: '10px 24px'}}
                >
                  Edit Subject Record
                </button>
              </div>
            </>
          );
        })()}
      </div>

      {/* CLASS / SEMESTER & SECTION ALLOCATION MODAL (REAL TEACHERS & REAL CLASSES/SEMESTERS ONLY) */}
      {showAllocationModal && selectedSubject && (
        <div className="fs-modal-overlay">
          <div className="fs-modal-card" style={{ maxWidth: '540px' }}>
            <div className="fs-header">
              <div>
                <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#0F172A' }}>
                  {isEditingAllocation 
                    ? (isDegreeCollege ? 'Edit Semester & Section Allocation' : isPuc ? 'Edit Stream & Section Allocation' : 'Edit Class & Section Allocation')
                    : (isDegreeCollege ? 'Manage Semester & Section Allocation' : isPuc ? 'Manage Stream & Section Allocation' : 'Manage Class & Section Allocation')}
                </h3>
                <div style={{ fontSize: '12.5px', color: '#64748B', marginTop: '3px' }}>
                  {isEditingAllocation 
                    ? (isDegreeCollege ? `Update Faculty Teacher & Periods/Week for ${selectedSubject.subjectName}` : isPuc ? `Update Lecturer & Periods/Week for ${selectedSubject.subjectName}` : `Update Teacher & Periods/Week for ${selectedSubject.subjectName}`)
                    : (isDegreeCollege ? `Assign Semester, Section, Faculty Teacher & Periods/Week for ${selectedSubject.subjectName}` : isPuc ? `Assign Stream / Year, Section, Lecturer & Periods/Week for ${selectedSubject.subjectName}` : `Assign Class, Section, Teacher & Periods/Week for ${selectedSubject.subjectName}`)}
                </div>
              </div>
              <button className="fs-close-btn" onClick={handleCloseAllocationModal}>✕</button>
            </div>

            <div className="fs-body" style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px', display: 'block' }}>
                  Subject to Allocate *
                </label>
                <select
                  className="form-control"
                  value={allocationForm.subjectId || ''}
                  disabled={isEditingAllocation}
                  onChange={(e) => {
                    const subId = parseInt(e.target.value, 10);
                    const match = displaySubjects.find(s => s.subjectId === subId);
                    let targetSem = '';
                    if (match) {
                      if (match.semester) {
                        const s = String(match.semester).trim();
                        targetSem = s.toLowerCase().startsWith('sem') ? s : `Sem ${s}`;
                      } else if (match.classLevel) {
                        targetSem = match.classLevel;
                      }
                    }
                    setAllocationForm(prev => ({
                      ...prev,
                      subjectId: String(subId),
                      subjectName: match ? match.subjectName : '',
                      className: targetSem || prev.className || (isDegreeCollege ? 'Sem 1' : 'Class 1')
                    }));
                    if (match) setSelectedSubject(match);
                  }}
                  style={{ height: '42px', borderRadius: '8px', width: '100%', fontSize: '13.5px' }}
                >
                  <option value="">-- Select Subject --</option>
                  {displaySubjects.map(sub => (
                    <option key={sub.subjectId} value={sub.subjectId}>
                      {sub.subjectName} ({sub.subjectCode}){sub.departmentName ? ` — ${sub.departmentName}` : ''}
                    </option>
                  ))}
                </select>

                {/* Subject Department & Program Context Card */}
                {selectedSubject && (
                  <div style={{ marginTop: '10px', padding: '10px 14px', background: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', display: 'flex', flexWrap: 'wrap', gap: '14px', fontSize: '12px', alignItems: 'center' }}>
                    {(selectedSubject.departmentName || selectedSubject.department) && (
                      <div>
                        <span style={{ color: '#64748B', fontWeight: '600' }}>Department: </span>
                        <span style={{ color: '#0F172A', fontWeight: '700' }}>{selectedSubject.departmentName || selectedSubject.department}</span>
                      </div>
                    )}
                    {(selectedSubject.courseCode || selectedSubject.courseName) && (
                      <div>
                        <span style={{ color: '#64748B', fontWeight: '600' }}>Program: </span>
                        <span style={{ color: '#2563EB', fontWeight: '700' }}>{selectedSubject.courseCode || selectedSubject.courseName}</span>
                      </div>
                    )}
                    {(selectedSubject.semester || selectedSubject.classLevel) && (
                      <div>
                        <span style={{ color: '#64748B', fontWeight: '600' }}>Configured: </span>
                        <span style={{ color: '#166534', fontWeight: '700' }}>
                          {selectedSubject.classLevel || (String(selectedSubject.semester).startsWith('Sem') ? selectedSubject.semester : `Sem ${selectedSubject.semester}`)}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
                <div>
                  <label style={{ fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px', display: 'block' }}>
                    {isDegreeCollege ? 'Target Semester *' : isPuc ? 'Target Stream / PUC Year *' : 'Target Class / Grade *'}
                  </label>
                  <select
                    className="form-control"
                    value={allocationForm.className}
                    disabled={isEditingAllocation}
                    onChange={(e) => setAllocationForm({...allocationForm, className: e.target.value})}
                    style={{ height: '42px', borderRadius: '8px', width: '100%', fontSize: '13.5px' }}
                  >
                    <option value="">
                      {isDegreeCollege ? '-- Select Semester --' : isPuc ? '-- Select Stream / Year --' : '-- Select Class --'}
                    </option>
                    {modalTargetClassNames.length > 0 ? (
                      modalTargetClassNames.map(cls => (
                        <option key={cls} value={cls}>{cls}</option>
                      ))
                    ) : (
                      <option value={isDegreeCollege ? "Sem 1" : isPuc ? "1st PUC" : "Class 1"}>
                        {isDegreeCollege ? "Sem 1" : isPuc ? "1st PUC" : "Class 1"}
                      </option>
                    )}
                  </select>
                </div>
 
                 <div>
                   <label style={{ fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px', display: 'block' }}>
                     Section *
                   </label>
                   <select
                     className="form-control"
                     value={allocationForm.section}
                     disabled={isEditingAllocation}
                     onChange={(e) => setAllocationForm({...allocationForm, section: e.target.value})}
                     style={{ height: '42px', borderRadius: '8px', width: '100%', fontSize: '13.5px' }}
                   >
                     <option value="">-- Select Section --</option>
                     {availableSections.map(sec => {
                       const secDisplay = sec.startsWith('Section ') ? sec : `Section ${sec}`;
                       return (
                         <option key={sec} value={sec}>{secDisplay}</option>
                       );
                     })}
                   </select>
                 </div>
               </div>
 
               <div style={{ marginBottom: '20px' }}>
                 <label style={{ fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px', display: 'block' }}>
                   {isDegreeCollege ? 'Assigned Faculty Teacher *' : isPuc ? 'Assigned Lecturer *' : 'Assigned Teacher *'}
                 </label>
                 <select
                   className="form-control"
                   value={allocationForm.teacherId || ''}
                   onChange={(e) => {
                     const tId = e.target.value;
                     const match = realTeachers.find(t => String(t.id || t.teacherId) === String(tId));
                     setAllocationForm({
                       ...allocationForm,
                       teacherId: tId,
                       teacherName: match ? match.name || match.teacherName : ''
                     });
                   }}
                   style={{ height: '42px', borderRadius: '8px', width: '100%', fontSize: '13.5px' }}
                 >
                   <option value="">
                     {isDegreeCollege ? '-- Select Faculty Teacher --' : isPuc ? '-- Select Lecturer --' : '-- Select Teacher --'}
                   </option>
                   {realTeachers.length > 0 ? (
                     realTeachers.map(t => (
                       <option key={t.id || t.teacherId} value={t.id || t.teacherId}>
                         {t.name || t.teacherName}
                       </option>
                     ))
                   ) : (
                     <option value="">-- No teachers available in database --</option>
                   )}
                 </select>
               </div>
 
               <div style={{ marginBottom: '20px' }}>
                 <label style={{ fontSize: '12.5px', fontWeight: '700', color: '#334155', marginBottom: '6px', display: 'block' }}>
                   Periods per Week *
                 </label>
                 <input
                   type="number"
                   className="form-control"
                   value={allocationForm.periodsPerWeek}
                   onChange={(e) => setAllocationForm({...allocationForm, periodsPerWeek: e.target.value})}
                   placeholder="e.g. 5"
                   min="1"
                   max="40"
                   style={{ height: '42px', borderRadius: '8px', width: '100%', fontSize: '13.5px' }}
                 />
               </div>
 
               <div style={{ padding: '14px', background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: '8px', fontSize: '12.5px', color: '#1E40AF' }}>
                 {isDegreeCollege 
                   ? 'ℹ️ Semester & Section allocations configure weekly period frequencies and faculty availability for the Timetable engine.'
                   : isPuc 
                     ? 'ℹ️ Stream & Section allocations configure weekly period frequencies and lecturer availability for the Timetable engine.'
                     : 'ℹ️ Class & Section allocations configure weekly period frequencies and teacher availability for the Timetable engine.'}
               </div>
             </div>
 
             <div className="fs-footer" style={{ padding: '16px 24px', display: 'flex', justifyContent: 'space-between' }}>
               <button type="button" className="fs-btn-secondary" onClick={handleCloseAllocationModal}>
                 Cancel
               </button>
               <button 
                 type="button" 
                 className="fs-btn-primary"
                 disabled={savingAllocation}
                 onClick={handleSaveClassAllocation}
               >
                 {savingAllocation ? 'Saving...' : (isEditingAllocation ? '✓ Update Allocation' : (isDegreeCollege ? '✓ Save Semester Allocation' : isPuc ? '✓ Save Stream Allocation' : '✓ Save Class Allocation'))}
               </button>
              </div>
            </div>
          </div>
        )}

      {/* HOVER & TRANSITION STYLES */}
      <style>{`
        .hover-card:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.08), 0 4px 6px -2px rgba(0, 0, 0, 0.04) !important;
        }
      `}</style>
    </div>
  );
};

export default SubjectManagement;
